import { migrateCookie } from './cookie/migration.js';
import { updateTouchData } from './attribution/touch.js';
import { incrementPageViews } from './page/page-views.js';
import {
  loadAmplitude,
  initAmplitude,
  amplitudeIdentify,
  saveAmplitudeDeviceId,
  setAmplitudeUserId,
} from './services/amplitude.js';
import { loadHubSpotScript, hsqIdentify } from './services/hubspot.js';
import { buildHubSpotIdentifyPayload } from './identify/hubspot-builder.js';
import { buildAmplitudeIdentifyCall } from './identify/amplitude-builder.js';
import { watchForForms } from './forms/waiter.js';
import { attachSubmitHandler } from './forms/interceptor.js';
import { handleThankYouPage } from './page/thank-you.js';
import { handleNewsletterUnsubPage } from './page/newsletter-unsub.js';
import { handleNotFoundPage } from './page/not-found.js';
import { handleCandidatPage } from './page/candidat.js';
import { onDOMReady } from './utils/dom-ready.js';
import { logger } from './utils/logger.js';
import { TRACKEEZ_VERSION } from './config.js';
import { getTaleezUser } from './cookie/taleez-user.js';
import { getTrackingData } from './cookie/tracking-cookie.js';

(function boot(): void {
  logger.info(`Trackeez v${TRACKEEZ_VERSION} init`);

  // ═══ Phase 1 — Synchrone (avant DOMContentLoaded) ═══
  try {
    migrateCookie();
    updateTouchData();

    // Log si l'utilisateur est un user Taleez connecté
    const taleezUser = getTaleezUser();
    if (taleezUser) {
      logger.info(`Taleez user connecté: id=${taleezUser.id}, company=${taleezUser.companyId}`);
    }

    incrementPageViews();
  } catch (error) {
    logger.error('Erreur phase 1 (cookie/attribution):', error);
    // On continue quand même — ne pas bloquer la suite
  }

  // ═══ Phase 2 — Après DOMContentLoaded ═══
  onDOMReady(() => {
    // Amplitude : load → init → setUserId → identify → save device id
    loadAmplitude(async () => {
      try {
        initAmplitude();

        // Déterminer l'email (priorité Taleez > email_identify_hs > email)
        const taleezUser = getTaleezUser();
        const data = getTrackingData();
        const email = taleezUser?.mail ?? data.email_identify_hs ?? data.email;

        // Poser le user_id AVANT l'identify pour que les user_properties
        // soient attachées au bon user dès le premier event
        await setAmplitudeUserId(email);

        amplitudeIdentify(buildAmplitudeIdentifyCall());
        saveAmplitudeDeviceId();
      } catch (error) {
        logger.error('Erreur init Amplitude:', error);
      }
    });

    // HubSpot tracking script
    loadHubSpotScript();

    // Premier _hsq identify au boot (async car hash email)
    (async () => {
      try {
        const payload = await buildHubSpotIdentifyPayload('boot');
        hsqIdentify(payload);
      } catch (error) {
        logger.error('Erreur _hsq identify au boot:', error);
      }
    })();

    // Intercepter tous les formulaires HSFC (détection fail-safe)
    watchForForms((form, formId, category) => {
      attachSubmitHandler(form, formId, category);
    });

    // Thank you page handling
    try {
      handleThankYouPage();
    } catch (error) {
      logger.error('Erreur thank you page:', error);
    }

    // Newsletter unsub page handling
    try {
      handleNewsletterUnsubPage();
    } catch (error) {
      logger.error('Erreur newsletter unsub page:', error);
    }

    // 404 page detection
    try {
      handleNotFoundPage();
    } catch (error) {
      logger.error('Erreur 404 page:', error);
    }

    // Page redirection candidat — tag Amplitude candidat=Oui
    try {
      handleCandidatPage();
    } catch (error) {
      logger.error('Erreur page redirection candidat:', error);
    }
  });
})();
